import { memo, useMemo } from "react"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faAlarmClock as faAlarmClockLight,
    faBadgePercent as faBadgePercentLight,
    faCalendarClock as faCalendarClockLight,
    faCalendarExclamation as faCalendarExclamationLight,
    faFileLines as faFileLinesLight,
    faTag as faTagLight,
    faUserLock as faUserLockLight,
    faWheat as faWheatLight,
} from "@fortawesome/pro-light-svg-icons"
import {
    faAlarmClock,
    faBadgePercent,
    faCalendarClock,
    faCalendarExclamation,
    faFileLines,
    faTag,
    faWheat,
    faUserLock,
} from "@fortawesome/pro-regular-svg-icons"
import { Box, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { toDinero } from "../../../../../currency/currencyLib"
import { useMoney } from "../../../../../currency/useMoney"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { formatWeekDays } from "../../helpers"
import { DiscountType } from "../../productsTypes"
import type { ProductItem, GroupItem, ProductGroupAvailability, ProductGroupSecurity } from "../../productsTypes"

const useStyles = makeStyles()((theme) => ({
    iconBox: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        padding: ".125rem .375rem",
        borderRadius: ".25rem",
        border: `1px solid ${tailwindPalette.slate[200]}`,
    },
    icon: {
        fontSize: ".75rem",
        color: tailwindPalette.slate[500],
    },
    tooltipPaper: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.tailwind.slate[900],
        padding: "1.5rem 1.75rem",
        boxShadow: theme.shadows[4],
        maxWidth: "28rem",
        borderRadius: ".5rem",
    },
    tooltipArrow: {
        color: theme.palette.common.white,
    },
    tooltipGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem 1.75rem",
    },
    tooltipGridFullWidth: {
        gridColumn: "1 / -1",
    },
    tooltipTitleRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
    },
    tooltipTitleIcon: {
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[500],
    },
    tooltipTitle: {
        fontWeight: 600,
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[700],
    },
    tooltipValue: {
        fontSize: ".875rem",
        whiteSpace: "pre-line",
        color: theme.palette.tailwind.slate[600],
        marginTop: ".1875rem",
    },
}))

type InfoIcon = Readonly<{
    icon: IconDefinition
    lightIcon: IconDefinition
    label: string
    value?: string
    spanFullWidth?: boolean
}>

function useProductInfoIcons(product: ProductItem): ReadonlyArray<InfoIcon> {
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()

    return useMemo(() => {
        const icons: InfoIcon[] = []

        if (product.deadline) {
            icons.push({
                icon: faAlarmClock,
                lightIcon: faAlarmClockLight,
                label: l10n.getString("product-info-icon-deadline-label"),
                value: l10n.getString("product-info-icon-deadline-value", {
                    daysBefore: product.deadline.purchases.daysBefore,
                    timeOfDay: product.deadline.purchases.timeOfDay,
                }),
            })
        }

        const availabilityWeekDays = product.availabilityDetails?.weekDays ?? []
        const availabilityTime = product.availabilityDetails?.timeInterval
        if (
            product.availabilityDetails?.dateInterval?.from ||
            product.availabilityDetails?.dateInterval?.to ||
            availabilityWeekDays.length ||
            availabilityTime
        ) {
            const formatApiDate = (value: string | undefined) => {
                const dateTime = dateTimeFactory.fromApiOrUndefined(value)
                return dateTime ? dateTimeFactory.formatDate(dateTime) : ""
            }
            const from = formatApiDate(product.availabilityDetails?.dateInterval?.from)
            const to = formatApiDate(product.availabilityDetails?.dateInterval?.to)
            const dates = from && to ? `${from} – ${to}` : from || to
            const days = formatWeekDays(l10n, availabilityWeekDays)
            const time = availabilityTime ? `${availabilityTime.start} – ${availabilityTime.end}` : ""
            icons.push({
                icon: faCalendarClock,
                lightIcon: faCalendarClockLight,
                label: l10n.getString("product-info-icon-availability-label"),
                value: [dates, days, time].filter(Boolean).join("\n"),
                spanFullWidth: Boolean(days),
            })
        }

        if (product.offerDetails?.discount?.type && product.offerDetails.discount.type !== DiscountType.NONE) {
            const discount = product.offerDetails.discount
            const discountedPriceLabel = discount.discountedPrice
                ? moneyFactory.format(toDinero(discount.discountedPrice))
                : undefined

            let value: string
            if (discount.type === DiscountType.DISCOUNT_PERCENTAGE && discount.percentage) {
                const percentageLabel = l10n.getString("product-info-icon-offer-percentage-value", {
                    percentage: discount.percentage.amount,
                })
                value = discountedPriceLabel ? `${percentageLabel}\n${discountedPriceLabel}` : percentageLabel
            } else {
                value = discountedPriceLabel ?? l10n.getString("product-info-icon-offer-discount-price-value")
            }

            icons.push({
                icon: faBadgePercent,
                lightIcon: faBadgePercentLight,
                label: l10n.getString("product-info-icon-offer-label"),
                value,
            })
        }

        if (product.productDescription?.marketingTag) {
            icons.push({
                icon: faTag,
                lightIcon: faTagLight,
                label: l10n.getString("product-info-icon-marketing-tag-label"),
                value: product.productDescription.marketingTag,
            })
        }

        if (product.productDescription?.allergens?.length) {
            icons.push({
                icon: faWheat,
                lightIcon: faWheatLight,
                label: l10n.getString("product-info-icon-allergens-label"),
                value: product.productDescription.allergens.map((a) => a.name).join(", "),
                spanFullWidth: true,
            })
        }

        if (product.availabilityDetails?.allowPreOrder === false) {
            icons.push({
                icon: faCalendarExclamation,
                lightIcon: faCalendarExclamationLight,
                label: l10n.getString("product-info-icon-allow-pre-order-label"),
                value: l10n.getString("no"),
            })
        }

        if (product.description) {
            icons.push({
                icon: faFileLines,
                lightIcon: faFileLinesLight,
                label: l10n.getString("product-info-icon-description-label"),
                value: product.description,
                spanFullWidth: true,
            })
        }

        return icons
    }, [product, l10n, moneyFactory, dateTimeFactory])
}

type ProductGroupIconData = Readonly<{
    availabilityDetails?: ProductGroupAvailability
    securitySettings?: ProductGroupSecurity
}>

function useProductGroupInfoIcons(data: ProductGroupIconData): ReadonlyArray<InfoIcon> {
    const { l10n } = useAppLocalization()

    return useMemo(() => {
        const icons: InfoIcon[] = []

        if (data.securitySettings?.isLocked) {
            icons.push({
                icon: faUserLock,
                lightIcon: faUserLockLight,
                label: l10n.getString("product-info-icon-pin-code-label"),
                value: l10n.getString("product-info-icon-pin-code-value"),
            })
        }

        if (data.availabilityDetails) {
            const days = formatWeekDays(l10n, data.availabilityDetails.weekDays)
            const time = `${data.availabilityDetails.timeInterval.start} – ${data.availabilityDetails.timeInterval.end}`
            icons.push({
                icon: faCalendarClock,
                lightIcon: faCalendarClockLight,
                label: l10n.getString("product-info-icon-availability-label"),
                value: `${days}\n${time}`,
                spanFullWidth: true,
            })
        }

        return icons
    }, [data, l10n])
}

type InfoIconsBoxProps = Readonly<{
    icons: ReadonlyArray<InfoIcon>
}>

const InfoIconsBox = memo(function InfoIconsBox({ icons }: InfoIconsBoxProps) {
    const { classes } = useStyles()

    if (icons.length === 0) return null

    const tooltipContent = (
        <Box className={classes.tooltipGrid}>
            {icons.map((entry) => (
                <Box key={entry.label} className={entry.spanFullWidth ? classes.tooltipGridFullWidth : undefined}>
                    <Box className={classes.tooltipTitleRow}>
                        <FontAwesomeIcon icon={entry.lightIcon} className={classes.tooltipTitleIcon} />
                        <Typography className={classes.tooltipTitle}>{entry.label}</Typography>
                    </Box>
                    {entry.value && <Typography className={classes.tooltipValue}>{entry.value}</Typography>}
                </Box>
            ))}
        </Box>
    )

    return (
        <Tooltip
            title={tooltipContent}
            arrow
            placement="bottom-start"
            classes={{ tooltip: classes.tooltipPaper, arrow: classes.tooltipArrow }}
        >
            <Box className={classes.iconBox}>
                {icons.map((entry) => (
                    <FontAwesomeIcon key={entry.label} icon={entry.icon} className={classes.icon} />
                ))}
            </Box>
        </Tooltip>
    )
})

type ProductInfoIconsProps = Readonly<{
    product: ProductItem
}>

export const ProductInfoIcons = memo(function ProductInfoIcons({ product }: ProductInfoIconsProps) {
    const icons = useProductInfoIcons(product)
    return <InfoIconsBox icons={icons} />
})

type ProductGroupInfoIconsProps = Readonly<{
    group: GroupItem
}>

export const ProductGroupInfoIcons = memo(function ProductGroupInfoIcons({ group }: ProductGroupInfoIconsProps) {
    const data: ProductGroupIconData = {
        availabilityDetails: group.groupAvailabilityDetails,
        securitySettings: group.groupSecuritySettings,
    }
    const icons = useProductGroupInfoIcons(data)
    return <InfoIconsBox icons={icons} />
})

type ProductGroupInfoIconsStandaloneProps = Readonly<{
    availabilityDetails?: ProductGroupAvailability
    securitySettings?: ProductGroupSecurity
}>

export const ProductGroupInfoIconsStandalone = memo(function ProductGroupInfoIconsStandalone(
    props: ProductGroupInfoIconsStandaloneProps,
) {
    const icons = useProductGroupInfoIcons(props)
    return <InfoIconsBox icons={icons} />
})
